import { useEffect, useMemo } from "react";
import { makeStyles } from "tss-react/mui";
import { PERMISSIONS } from "@miplatform/constants";
import { getCurrencyString } from "@miplatform/utils";
import { usePermissions } from "@mibuildweb/hooks";
import { Box, Grid, Typography } from "@mui/material";
import {
  COLUMN_WIDTH,
  IDrawRow,
  getColumns,
  getDrawValue,
} from "../../../utils/priceDetails";
import { useDrawsSummaryViewModel } from "./useDrawsSummaryViewModel";

export function DrawsSummary() {
  const { classes, cx } = useStyles();
  const { hasPermission } = usePermissions();
  const canReadPricing = hasPermission(PERMISSIONS.PRICING.READ);
  const { planAreas, priceDetails, getPriceDetails } =
    useDrawsSummaryViewModel();

  useEffect(() => {
    getPriceDetails();
  }, [planAreas]);

  const columns = useMemo(() => getColumns(priceDetails), [priceDetails]);

  if (!canReadPricing || !priceDetails) {
    return null;
  }

  const renderDraws = (row: IDrawRow) => (
    <Grid container wrap="nowrap" spacing={1}>
      {columns.map((column) => {
        const value = getDrawValue(row, column);
        return (
          <Grid key={column} className={classes.columnWidth}>
            <Box className={classes.drawCell}>
              <Typography
                className={cx(classes.mediumText, classes.truncatedText)}
              >
                {column}
              </Typography>
              <Typography className={classes.mediumText}>
                {getCurrencyString(value ?? 0)}
              </Typography>
            </Box>
          </Grid>
        );
      })}
      <Grid className={classes.columnWidth}>
        <Box className={cx(classes.drawCell, classes.totalDrawCell)}>
          <Typography className={classes.mediumText}>Total</Typography>
          <Typography className={classes.mediumText}>
            {getCurrencyString(row.total)}
          </Typography>
        </Box>
      </Grid>
    </Grid>
  );

  return (
    <Box className={classes.drawsWrapper}>{renderDraws(priceDetails)}</Box>
  );
}

const useStyles = makeStyles()((theme) => ({
  drawsWrapper: {
    display: "flex",
    alignItems: "center",
  },
  drawCell: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    boxSizing: "border-box",
    borderRadius: 4,
    backgroundColor: theme.palette.primary.light,
    height: 36,
    overflow: "hidden",
    padding: theme.spacing(0, 0.5),
  },
  totalDrawCell: {
    backgroundColor: theme.palette.green.light,
  },
  mediumText: {
    lineHeight: 1.25,
    fontSize: theme.fontSize.medium,
  },
  truncatedText: {
    maxWidth: "100%",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  columnWidth: {
    width: COLUMN_WIDTH,
  },
}));
